/**
 * Lazy React runtime loader.
 *
 * Dynamic-imports react and react-dom/client via bare imports so
 * the browser's import map resolves them to the shared vendor
 * bundle at runtime. The promise is memoized on the module level
 * so every component mount reuses the same React instance -
 * without that, two concurrent mounts could end up with separate
 * React runtimes and hook calls would throw `Invalid hook call`.
 *
 * In dev mode the imports target Vite's pre-bundled deps instead
 * of the production vendor bundle, matching what Vite serves to
 * framework modules so hook identity is preserved across the
 * app-module / component-module boundary.
 *
 * The @vite-ignore comment instructs esbuild's metafile emitter
 * to leave these dynamic imports untouched in the bundled output;
 * combined with packages: 'external' in the compiler config, bare
 * and runtime-templated imports both survive bundling.
 */

import type { ReactLike, ReactDomLike } from './types';

type ReactPair = [ReactLike, ReactDomLike];

let reactPromise: Promise<ReactPair> | null = null;

export function loadReact(devServer: string | null): Promise<ReactPair> {
  if (reactPromise) {
    return reactPromise;
  }

  if (devServer) {
    reactPromise = Promise.all([
      import(/* @vite-ignore */ `${devServer}/node_modules/.vite/deps/react.js`) as Promise<ReactLike>,
      import(/* @vite-ignore */ `${devServer}/node_modules/.vite/deps/react-dom_client.js`) as Promise<ReactDomLike>,
    ]);
  }
  else {
    reactPromise = Promise.all([
      import('react') as Promise<ReactLike>,
      import('react-dom/client') as Promise<ReactDomLike>,
    ]);
  }
  return reactPromise;
}

/**
 * Normalizes a dynamically-imported module that may expose its
 * exports either as ESM named exports (`module.createElement`)
 * or as a CJS default export (`module.default.createElement`).
 */
export function unwrapModule<T extends { default?: T }>(module: T): T {
  return module.default ?? module;
}
